import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import BottomNav from '../components/BottomNav'
import LoadingSpinner from '../components/LoadingSpinner'
import SubmitButton from '../components/SubmitButton'
import SearchBar from '../components/SearchBar'
import TextListBottomSheet from '../components/TextListBottomSheet'
import ConfirmDialog from '../components/ConfirmDialog'
import { BOTTOM_NAV_HEIGHT } from '../constants/layout'
import { UNIVERSITY_BADGE, inviteUniversityFriends } from '../constants/university'
import {
  getUniversityGroup,
  universityApi,
  UniversityGroup as Group,
  UniversityGroupMember,
} from '../api/university'
import { toastUtil } from '../utils/toast'
import { REFERRAL_READY } from '../constants/featureFlags'

/**
 * 학교 그룹 — 같은 학교 인증자들의 기록 수 순위 (Figma `U6 그룹 홈 · 리더보드`,
 * 최신본 6217:9476 / 멤버 1명일 때 6217:10087).
 *
 * 누가 볼 수 있는지·누구를 셀지는 전부 서버가 정한다(UniversityController::group).
 * 인증 안 한 사람은 서버가 403 을 주고, 그러면 학교 고르기로 보낸다.
 *
 * 우상단 '설정'(2026-11-04, Figma `그룹 설정 시트` 6434:13563 · `소속 해제` 6433:13534):
 * 친구 초대 · 소속 해제 두 줄. 시트와 확인창은 앱의 공용 부품을 그대로 쓴다.
 * 해제하면 홈으로 가고, 같은 학교로는 메일 인증 없이 돌아온다(/university/rejoin).
 *
 * 뒤로가기는 언제나 홈이다. 인증 완료·친구 초대 화면을 지나 왔어도 그쪽으로
 * 돌아가지 않는다.
 *
 * 아직 안 넣은 것: 정렬 바꾸기(지금은 기록 수 하나뿐이라 글자만 둔다).
 */
const BROWN = 'text-brown-80%-main'

const UniversityGroup: React.FC = () => {
  const navigate = useNavigate()
  const [group, setGroup] = useState<Group | null>(null)
  const [failed, setFailed] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)

  const load = () => {
    setFailed(false)
    getUniversityGroup()
      .then(setGroup)
      .catch((e) => {
        if (e?.response?.status === 403) {
          navigate('/university', { replace: true })
          return
        }
        setFailed(true)
      })
  }

  useEffect(load, []) // eslint-disable-line react-hooks/exhaustive-deps

  const members = useMemo(() => {
    const list = group?.members ?? []
    const q = query.trim().toLowerCase()
    return q ? list.filter((m) => (m.nickname ?? '').toLowerCase().includes(q)) : list
  }, [group, query])

  // 나는 마이 화면을 연다 — 탭 첫 화면이라 뒤로가기가 없으니 달아 달라고 알린다
  const openProfile = (m: UniversityGroupMember) => {
    if (m.isMe) navigate('/main-my', { state: { showBack: true } })
    else navigate(`/user/${m.userId}/profile`)
  }

  const row = (m: UniversityGroupMember) => (
    <li key={m.userId}>
      <button
        type="button"
        onClick={() => openProfile(m)}
        className="w-full flex items-center gap-8px py-16px active:opacity-60"
      >
        <span
          className={`shrink-0 w-45px text-center font-pretendard font-bold text-24px leading-[20px] tracking-[-0.72px] ${BROWN}`}
        >
          {m.rank}
        </span>
        <img
          src={m.profileImage || '/images/profile-placeholder-icon.png'}
          alt=""
          className="shrink-0 w-40px h-40px rounded-full object-cover bg-[#f1e9dc]"
        />
        {/* 사진과 닉네임 사이는 12px — 프로필 화면과 같게 (줄 간격 8 + 4) */}
        <span className="flex-1 min-w-0 ml-4px flex items-center gap-6px">
          <span
            className={`truncate font-pretendard font-bold text-15px leading-140% tracking-[-0.15px] ${BROWN}`}
          >
            {m.nickname || '이름 없음'}
          </span>
          {m.isMe && (
            <span className="shrink-0 px-7px py-1px rounded-full bg-brown-80%-main font-pretendard font-bold text-11px leading-140% text-white">
              나
            </span>
          )}
        </span>
        <span className={`shrink-0 pr-8px font-pretendard font-bold text-16px leading-140% ${BROWN}`}>
          {m.recordCount.toLocaleString()}
        </span>
      </button>
      <div className="h-px bg-divider-divider-1" />
    </li>
  )

  const invite = () => {
    if (REFERRAL_READY) navigate('/invite')
    else if (group) inviteUniversityFriends(group.university.name, toastUtil)
  }

  const goHome = () => navigate('/main-home', { replace: true })

  const leave = async () => {
    if (leaving) return
    setLeaving(true)
    try {
      await universityApi.leave()
      setLeaveOpen(false)
      goHome()
    } catch {
      toastUtil.error('잠시 후 다시 시도해주세요')
      setLeaving(false)
    }
  }

  const badge = group ? UNIVERSITY_BADGE[group.university.key] ?? group.university.logoPath : null
  const alone = !!group && group.memberCount <= 1

  return (
    <div className="w-full min-h-full flex flex-col bg-backgroundColorBg2">
      <AppBar
        title="대학교 그룹"
        isBottomBorder
        onBack={goHome}
        rightElement={
          group ? (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              // 앱바 우측 글자 버튼 공통 모양 (Collect '방법'·랭킹 '수정'과 같다)
              className="text-style-button1 text-textColorText2 cursor-pointer hover:opacity-70 px-16px h-48px whitespace-nowrap"
            >
              설정
            </button>
          ) : null
        }
      />

      <div
        className="app-container flex-1 flex flex-col px-20px pt-16px"
        style={{ paddingBottom: `calc(${BOTTOM_NAV_HEIGHT + 24}px + env(safe-area-inset-bottom))` }}
      >
        {failed ? (
          <div className="py-80px flex flex-col items-center gap-12px">
            <p className="text-style-body1 text-textColorText3">잠시 후 다시 시도해주세요</p>
            <button
              type="button"
              onClick={load}
              className="text-style-button2 text-textColorText2 underline"
            >
              다시 불러오기
            </button>
          </div>
        ) : !group ? (
          <div className="py-80px flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/*
              학교 헤더. 학교 이름은 화면 맨 위 제목('대학교 그룹', AppBar)과 같은 글씨다 —
              text-style-labelL 16px. 도연님 지정(2026-11-03), 목표 이미지 실측과도 맞다.
            */}
            <div className="flex items-center gap-12px">
              {badge && (
                <img src={badge} alt="" className="shrink-0 w-52px h-52px rounded-full object-cover" />
              )}
              <div className="flex-1 min-w-0 flex flex-col gap-1px">
                <p className="text-style-labelL text-16px text-brown-80%-main truncate">
                  {group.university.name}
                </p>
                <p className={`font-pretendard font-regular text-[13.5px] leading-140% tracking-[-0.135px] ${BROWN}`}>
                  멤버 {group.memberCount.toLocaleString()}명
                </p>
              </div>
            </div>

            {/* 정렬 · 검색 — 랭킹(MainRanking renderFilterSection)과 같은 아이콘·투명도·여백(18px) */}
            <div className="mt-16px -mx-2px h-28px flex items-center">
              <img src="/images/filter-sort-icon.png" alt="" className="w-28px h-28px object-contain" />
              <span className="font-pretendard font-regular text-16px leading-125% tracking-[-0.48px] text-[#4b3621]">
                기록 수
              </span>
              <div className="flex-1" />
              <button
                type="button"
                aria-label="멤버 검색"
                onClick={() => {
                  setSearchOpen((v) => !v)
                  setQuery('')
                }}
                className="flex items-center justify-center"
              >
                <img src="/images/ranking-search-icon.png" alt="" className="w-28px h-28px object-contain" />
              </button>
            </div>

            {/* 앱의 다른 검색창(홈·팔로우 요청)과 같은 SearchBar 를 쓴다 */}
            {searchOpen && (
              <div className="mt-12px w-full">
                <SearchBar placeholder="검색" value={query} onSearch={setQuery} height={44} />
              </div>
            )}

            <ul className="mt-4px flex flex-col">{members.map(row)}</ul>

            {searchOpen && query && members.length === 0 && (
              <div className="flex-1 flex items-center justify-center pb-80px">
                <p className="text-center text-style-body2 text-textColorText3">찾는 멤버가 없어요</p>
              </div>
            )}

            {alone && !query && (
              <div className="pt-96px flex flex-col items-center gap-16px">
                <p className={`font-pretendard font-bold text-15px leading-140% ${BROWN}`}>
                  친구를 초대해 같이 기록해보세요
                </p>
                <div className="w-[220px]">
                  <SubmitButton
                    text="친구 초대하기"
                    onSubmit={invite}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <TextListBottomSheet
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        items={[
          { text: '친구 초대', onClick: invite },
          { text: '소속 해제', onClick: () => setLeaveOpen(true) },
        ]}
      />

      <ConfirmDialog
        isOpen={leaveOpen}
        message={'학교 소속을 해제할까요?\n해제하면 랭킹과 멤버 목록에서 사라져요'}
        confirmText="해제"
        onConfirm={leave}
        onCancel={() => setLeaveOpen(false)}
      />

      <BottomNav activePath="" />
    </div>
  )
}

export default UniversityGroup
